import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Users, DollarSign, ArrowRight } from 'lucide-react';
import axios from 'axios';

// Get the API URL from the environment or use the default
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3011/api';

const mockProjects = [
  {
    _id: "mock-1",
    title: "Emergency Plasma & Platelet Camp",
    description: "Support our urgent campaign to establish plasma and platelet storage camps across metropolitan areas to meet the high demand.",
    image: "/images/1.jpeg",
    location: "New Delhi & NCR",
    startDate: new Date().toISOString(),
    beneficiaries: 1200,
    goalType: "Fundraising",
    goalAmount: 85000,
    raisedAmount: 54000,
    status: "Active",
    ngo: {
      name: "Lifesavers Foundation",
      logo: "/images/logo2.png"
    }
  },
  {
    _id: "mock-2",
    title: "Rural Blood Collection Drive",
    description: "Bringing safety-verified blood donation camps directly to rural communities in Haryana. Help fund transport and sterile kits.",
    image: "/images/2.jpeg",
    location: "Yamuna Nagar, Haryana",
    startDate: new Date(Date.now() - 86400000 * 3).toISOString(),
    beneficiaries: 650,
    goalType: "Fundraising",
    goalAmount: 45000,
    raisedAmount: 18000,
    status: "Active",
    ngo: {
      name: "Haryana Health NGO",
      logo: "/images/logo2.png"
    }
  },
  {
    _id: "mock-3",
    title: "Thalassemia Support Campaign",
    description: "Dedicated to providing weekly blood transfusions for children suffering from severe Thalassemia. Sponsor a child today.",
    image: "/images/3.jpeg",
    location: "All India Support",
    startDate: new Date(Date.now() - 86400000 * 7).toISOString(),
    beneficiaries: 2400,
    goalType: "Fundraising",
    goalAmount: 120000,
    raisedAmount: 95000,
    status: "Active",
    ngo: {
      name: "Hope Care NGO",
      logo: "/images/logo2.png"
    }
  }
];

const FeaturedProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visibleProjects, setVisibleProjects] = useState(3); // Number of projects to display

  // Fetch featured projects
  useEffect(() => {
    const fetchFeaturedProjects = async () => {
      try {
        setLoading(true);
        // Use the public endpoint that doesn't require authentication
        const response = await axios.get(`${API_URL}/public/projects/featured`);
        
        if (response.data && response.data.length > 0) {
          // Sort projects by date (newest first)
          const sortedProjects = response.data.sort((a, b) => {
            return new Date(b.createdAt || b.startDate) - new Date(a.createdAt || a.startDate);
          });
          setProjects(sortedProjects);
        } else {
          setProjects(mockProjects);
        }
        setLoading(false);
      } catch (err) {
        console.warn('API down, falling back to high-quality mock campaigns:', err);
        setProjects(mockProjects);
        setLoading(false);
      }
    };

    fetchFeaturedProjects();
  }, []);
  
  // Show more projects
  const showMoreProjects = () => {
    setVisibleProjects(prev => Math.min(prev + 3, projects.length));
  };
  
  // Check if there are more projects to show
  const hasMoreProjects = visibleProjects < projects.length;

  // Format date to readable string
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (loading) {
    return (
      <section className="bg-white py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse flex flex-col">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3 mb-8"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-gray-100 rounded-lg overflow-hidden shadow-md">
                  <div className="h-48 bg-gray-200"></div>
                  <div className="p-4">
                    <div className="h-6 bg-gray-200 rounded w-3/4 mb-3"></div>
                    <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3 mb-4"></div>
                    <div className="h-8 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="bg-white py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <p className="text-red-500">{error}</p>
        </div>
      </section>
    );
  }

  if (projects.length === 0) {
    return null; // Don't show the section if there are no featured projects
  }

  return (
    <section className="bg-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Featured Campaigns
          </h2>
          <p className="mt-3 max-w-2xl mx-auto text-xl text-gray-500 sm:mt-4">
            Support these impactful campaigns from our partner NGOs
          </p>
        </div>

        {/* Project Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {projects.slice(0, visibleProjects).map((project) => (
            <div key={project._id} className="bg-white rounded-lg overflow-hidden shadow-md border border-gray-100 hover:shadow-lg transition-shadow duration-300">
              {/* Project Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={project.image || 'https://via.placeholder.com/800x600?text=Campaign+Image'}
                  alt={project.title}
                  className="w-full h-full object-cover"
                />
                {project.ngo && project.ngo.logo && (
                  <div className="absolute top-2 left-2 bg-white p-1 rounded-full shadow-md">
                    <img
                      src={project.ngo.logo}
                      alt={project.ngo.name || 'NGO'}
                      className="h-8 w-8 rounded-full object-cover"
                    />
                  </div>
                )}
                {project.status && (
                  <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                    {project.status}
                  </div>
                )}
              </div>
              
              {/* Project Details */}
              <div className="p-4">
                <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-1">{project.title}</h3>
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">{project.description}</p>
                
                {/* Project Metadata */}
                <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
                  <div className="flex items-center text-gray-600">
                    <MapPin size={14} className="mr-1 text-red-500" />
                    <span className="truncate">{project.location || 'Various Locations'}</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Calendar size={14} className="mr-1 text-red-500" />
                    <span>{formatDate(project.startDate)}</span>
                  </div>
                  {project.beneficiaries && (
                    <div className="flex items-center text-gray-600">
                      <Users size={14} className="mr-1 text-red-500" />
                      <span>{project.beneficiaries}</span>
                    </div>
                  )}
                  {project.goalType === 'Fundraising' && (
                    <div className="flex items-center text-gray-600">
                      <DollarSign size={14} className="mr-1 text-red-500" />
                      <span>₹{project.goalAmount?.toLocaleString() || '0'}</span>
                    </div>
                  )}
                </div>
                
                {/* Progress Bar for Fundraising Projects */}
                {project.goalType === 'Fundraising' && (
                  <div className="mb-3">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-red-600 h-2 rounded-full" 
                        style={{ width: `${Math.min(100, ((project.raisedAmount || 0) / (project.goalAmount || 1)) * 100)}%` }}
                      ></div>
                    </div>
                    <div className="flex justify-between text-xs mt-1">
                      <span className="font-medium">₹{(project.raisedAmount || 0).toLocaleString()} raised</span>
                      <span className="text-gray-600">{Math.round(((project.raisedAmount || 0) / (project.goalAmount || 1)) * 100)}% of ₹{(project.goalAmount || 0).toLocaleString()}</span>
                    </div>
                  </div>
                )}
                
                {/* Action Buttons */}
                <div className="flex space-x-2">
                  <Link
                    to={`/projects/${project._id}`}
                    className="flex-1 text-center px-3 py-2 text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 transition-colors"
                  >
                    View Campaign
                  </Link>
                  {project.goalType === 'Fundraising' && (
                    <Link
                      to={`/projects/${project._id}/donate`}
                      className="flex-1 text-center px-3 py-2 text-xs font-medium rounded-md text-red-600 bg-white border border-red-600 hover:bg-red-50 transition-colors"
                    >
                      Donate Now
                    </Link>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* See More Button */}
        {hasMoreProjects && (
          <div className="text-center">
            <button 
              onClick={showMoreProjects}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
            >
              See More
              <ArrowRight size={16} className="ml-2" />
            </button>
          </div>
        )}
        
        {/* View All Projects Link */}
        <div className="text-center mt-6">
          <Link 
            to="/projects"
            className="inline-flex items-center text-red-600 hover:text-red-800 font-medium"
          >
            View All Campaigns
            <ArrowRight size={16} className="ml-1" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FeaturedProjects;
